/**
 * Super-admin — Edit subscription plans.
 *
 * GET  /api/super-admin/plans            → list plans (defaults + DB overrides)
 * PUT  /api/super-admin/plans/{id}       → update price/name/features/popular
 * POST /api/super-admin/plans/reset      → reset all plans to defaults
 *
 * Cada card é editável inline: nome, preço, "popular", e lista de features.
 */
import React, { useEffect, useState } from "react";
import { uid } from "../lib/uid";
import {
  CreditCard,
  RefreshCw,
  Save,
  Plus,
  X,
  Star,
  RotateCcw,
  Check,
  History,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useConfirm } from "../hooks/useConfirm";

export default function SuperAdminPlans() {
  const confirm = useConfirm();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyPlan, setHistoryPlan] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/plans");
      setPlans(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function resetAll() {
    const ok = await confirm({
      title: "Restaurar valores padrão?",
      description:
        "Isso apaga todas as edições feitas via DB e volta para Starter $19 / Pro $49 / Enterprise $149. Empresas já ativas não são afetadas.",
      destructive: true,
      actionLabel: "Restaurar",
    });
    if (!ok) return;
    try {
      await api.post("/super-admin/plans/reset");
      toast.success("Planos restaurados ao padrão");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    }
  }

  return (
    <div className="space-y-6" data-testid="super-admin-plans-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Planos & Preços</h1>
          <p className="text-sm text-slate-400 mt-1">
            Edite os valores cobrados por mês e o que cada plano oferece. As
            mudanças entram em vigor imediatamente para novos checkouts.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={resetAll}
            data-testid="reset-plans-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-sm font-semibold text-amber-200"
          >
            <RotateCcw size={14} /> Restaurar padrão
          </button>
          <button
            onClick={load}
            data-testid="refresh-plans-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm font-semibold text-slate-200"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
        </div>
      </header>

      {loading ? (
        <div className="p-10 text-center text-slate-400">Carregando…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((p) => (
            <PlanCard
              key={p.id}
              plan={p}
              onSaved={load}
              onHistory={() => setHistoryPlan(p)}
            />
          ))}
        </div>
      )}

      {historyPlan && (
        <PriceHistoryModal
          plan={historyPlan}
          onClose={() => setHistoryPlan(null)}
        />
      )}
    </div>
  );
}

function PlanCard({ plan, onSaved, onHistory }) {
  const [form, setForm] = useState({
    name: plan.name || "",
    price_usd: plan.price_usd ?? 0,
    interval_days: plan.interval_days ?? 30,
    popular: !!plan.popular,
    features: Array.isArray(plan.features) ? [...plan.features] : [],
    max_collaborators: plan.max_collaborators ?? "",
    max_clients: plan.max_clients ?? "",
    max_teams: plan.max_teams ?? "",
  });
  const [saving, setSaving] = useState(false);
  const numEq = (a, b) => {
    const aa = a === "" || a === null || a === undefined ? null : Number(a);
    const bb = b === "" || b === null || b === undefined ? null : Number(b);
    return aa === bb;
  };
  const dirty =
    form.name !== plan.name ||
    Number(form.price_usd) !== Number(plan.price_usd ?? 0) ||
    Number(form.interval_days) !== Number(plan.interval_days ?? 30) ||
    !!form.popular !== !!plan.popular ||
    JSON.stringify(form.features) !==
      JSON.stringify(Array.isArray(plan.features) ? plan.features : []) ||
    !numEq(form.max_collaborators, plan.max_collaborators) ||
    !numEq(form.max_clients, plan.max_clients) ||
    !numEq(form.max_teams, plan.max_teams);

  async function save() {
    setSaving(true);
    try {
      const toNullOrNum = (v) =>
        v === "" || v === null || v === undefined ? null : Number(v);
      await api.put(`/super-admin/plans/${plan.id}`, {
        name: form.name.trim(),
        price_usd: Number(form.price_usd),
        interval_days: Number(form.interval_days),
        popular: !!form.popular,
        features: form.features.map((s) => s.trim()).filter(Boolean),
        max_collaborators: toNullOrNum(form.max_collaborators),
        max_clients: toNullOrNum(form.max_clients),
        max_teams: toNullOrNum(form.max_teams),
      });
      toast.success(`Plano "${form.name}" atualizado`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha");
    } finally {
      setSaving(false);
    }
  }

  const [featureKeys, setFeatureKeys] = useState(() => form.features.map(uid));

  function setFeature(i, val) {
    setForm((f) => {
      const features = [...f.features];
      features[i] = val;
      return { ...f, features };
    });
  }
  function addFeature() {
    setForm((f) => ({ ...f, features: [...f.features, ""] }));
    setFeatureKeys((k) => [...k, uid()]);
  }
  function removeFeature(i) {
    setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
    setFeatureKeys((k) => k.filter((_, idx) => idx !== i));
  }

  return (
    <div
      className={`rounded-2xl border bg-gradient-to-br p-5 ${
        form.popular
          ? "from-fuchsia-500/15 to-violet-500/5 border-fuchsia-500/40"
          : "from-white/5 to-white/0 border-white/10"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 text-violet-300 flex items-center justify-center">
            <CreditCard size={18} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              {plan.id}
            </div>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="bg-transparent text-lg font-bold text-white outline-none border-b border-transparent focus:border-violet-500 transition"
              data-testid={`plan-name-${plan.id}`}
            />
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={form.popular}
            onChange={(e) => setForm((f) => ({ ...f, popular: e.target.checked }))}
            data-testid={`plan-popular-${plan.id}`}
          />
          <Star size={12} className="text-fuchsia-300" />
          Popular
        </label>
      </div>

      <div className="flex items-baseline gap-1 mb-3">
        <span className="text-xs text-slate-400">$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={form.price_usd}
          onChange={(e) =>
            setForm((f) => ({ ...f, price_usd: e.target.value }))
          }
          className="w-28 bg-transparent text-3xl font-bold text-white outline-none border-b border-white/10 focus:border-violet-500 transition"
          data-testid={`plan-price-${plan.id}`}
        />
        <span className="text-sm text-slate-400">/</span>
        <input
          type="number"
          min="1"
          value={form.interval_days}
          onChange={(e) =>
            setForm((f) => ({ ...f, interval_days: e.target.value }))
          }
          className="w-14 bg-transparent text-sm text-slate-300 outline-none border-b border-white/10 focus:border-violet-500 transition text-center"
          data-testid={`plan-interval-${plan.id}`}
        />
        <span className="text-sm text-slate-400">dias</span>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
          Recursos incluídos
        </div>
        {form.features.map((f, i) => (
          <div key={featureKeys[i] || `feat-${i}`} className="flex items-center gap-1.5">
            <Check size={13} className="text-emerald-400 flex-shrink-0" />
            <input
              value={f}
              onChange={(e) => setFeature(i, e.target.value)}
              className="flex-1 bg-transparent text-sm text-slate-200 outline-none border-b border-white/5 focus:border-violet-500 transition"
              data-testid={`plan-feature-${plan.id}-${i}`}
            />
            <button
              onClick={() => removeFeature(i)}
              className="p-1 rounded hover:bg-white/10 text-slate-500 hover:text-red-300"
              data-testid={`plan-feature-remove-${plan.id}-${i}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          onClick={addFeature}
          data-testid={`plan-feature-add-${plan.id}`}
          className="inline-flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200 font-semibold pt-1"
        >
          <Plus size={12} /> Adicionar recurso
        </button>
      </div>

      {/* Per-plan limits — dynamic, used by tenant enforcement */}
      <div
        className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2"
        data-testid={`plan-limits-${plan.id}`}
      >
        <div className="text-[10px] uppercase tracking-widest text-violet-200 font-bold">
          Limites do plano
        </div>
        <p className="text-[11px] text-slate-400 leading-snug">
          Aplicados automaticamente a todas as empresas neste plano. <b>0</b> ou
          vazio = ilimitado. Override por empresa pelo modal de Limites.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <LimitInput
            label="Clientes"
            value={form.max_clients}
            onChange={(v) => setForm((f) => ({ ...f, max_clients: v }))}
            testid={`plan-max-clients-${plan.id}`}
          />
          <LimitInput
            label="Colaboradores"
            value={form.max_collaborators}
            onChange={(v) => setForm((f) => ({ ...f, max_collaborators: v }))}
            testid={`plan-max-collaborators-${plan.id}`}
          />
          <LimitInput
            label="Equipes"
            value={form.max_teams}
            onChange={(v) => setForm((f) => ({ ...f, max_teams: v }))}
            testid={`plan-max-teams-${plan.id}`}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={onHistory}
          data-testid={`plan-history-${plan.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-xs font-semibold text-slate-200"
          title="Ver histórico de mudanças de preço"
        >
          <History size={13} /> Histórico
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid={`plan-save-${plan.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition ${
            dirty
              ? "bg-violet-600 hover:bg-violet-700 text-white"
              : "bg-white/5 text-slate-500 cursor-not-allowed"
          }`}
        >
          <Save size={14} />
          {saving ? "Salvando…" : dirty ? "Salvar alterações" : "Sem alterações"}
        </button>
      </div>
    </div>
  );
}


function PriceHistoryModal({ plan, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/super-admin/plans/${plan.id}/history`);
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled)
          setError(formatApiError(e.response?.data?.detail) || "Falha");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan.id]);

  function fmtDate(iso) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full text-white max-h-[85vh] flex flex-col"
        data-testid="plan-history-modal"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-wider text-violet-300 font-semibold flex items-center gap-1.5">
              <History size={12} /> Histórico de preços
            </div>
            <h2 className="text-lg font-bold text-white">
              {plan.name} <span className="text-slate-400 font-normal">— atual ${plan.price_usd}</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400"
            data-testid="plan-history-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {rows === null && !error && (
            <div className="text-center text-slate-400 py-6">Carregando…</div>
          )}
          {error && (
            <div className="text-center text-red-300 py-6 text-sm">{error}</div>
          )}
          {rows && rows.length === 0 && (
            <div className="text-center text-slate-400 py-6 text-sm">
              Ainda não houve mudanças de preço registradas para este plano.
            </div>
          )}
          {rows && rows.length > 0 && (
            <ol className="space-y-3" data-testid="plan-history-list">
              {rows.map((r, i) => {
                const up = r.new_price > r.old_price;
                return (
                  <li
                    key={r.id || i}
                    className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-white/5"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        up
                          ? "bg-red-500/20 text-red-300"
                          : "bg-emerald-500/20 text-emerald-300"
                      }`}
                    >
                      {up ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-slate-400 text-sm line-through">
                          ${Number(r.old_price).toFixed(2)}
                        </span>
                        <span className="text-slate-500">→</span>
                        <span className="text-white text-base font-bold">
                          ${Number(r.new_price).toFixed(2)}
                        </span>
                        {r.delta_pct != null && (
                          <span
                            className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                              up
                                ? "bg-red-500/15 text-red-300"
                                : "bg-emerald-500/15 text-emerald-300"
                            }`}
                          >
                            {up ? "+" : ""}
                            {r.delta_pct}%
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 mt-1">
                        {fmtDate(r.changed_at)} ·{" "}
                        <span className="text-slate-300">
                          {r.changed_by_name || r.changed_by_email || "—"}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function LimitInput({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
        {label}
      </span>
      <input
        type="number"
        min="0"
        inputMode="numeric"
        value={value === null || value === undefined ? "" : value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="∞"
        data-testid={testid}
        className="mt-1 w-full bg-slate-950/40 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white text-center font-mono focus:outline-none focus:border-violet-400"
      />
    </label>
  );
}

